import type { CoverageEntry } from '../api/masterCertificateApi';

// ── Form data shape exposed via forwardRef ────────────────────────────────────

export interface Acord24FormData {
  // Producer / contact
  producerAddress: string;
  producerCity: string;
  producerStreet: string;
  producerState: string;
  producerZip: string;
  contactName: string;
  contactPhone: string;
  contactFax: string;
  contactEmail: string;
  producerCustomerId: string;
  // Insured
  insuredName: string;
  insuredAddress: string;
  insuredCity: string;
  insuredState: string;
  insuredZip: string;
  // Insurers A–F (names and carrier databaseIds)
  insurerA: string; insurerB: string; insurerC: string;
  insurerD: string; insurerE: string; insurerF: string;
  insurerAId: string | null; insurerBId: string | null; insurerCId: string | null;
  insurerDId: string | null; insurerEId: string | null; insurerFId: string | null;
  // Cert header
  certDate: Date | null;
  certNumber: string;
  revisionNumber: string;
  locationProperty: string;
  descriptionOfProperty: string;
  specialConditions: string;
  // Property
  propInsurerLtr: string;
  propChecked: boolean;
  propEarthquakeDeduct: string;
  propWindDeduct: string;
  propFloodDeduct: string;
  propOther1Text: string;
  propOther2Text: string;
  propPolicyNumber: string;
  propEffDate: Date | null;
  propExpDate: Date | null;
  propBasic: boolean;
  propBroad: boolean;
  propSpecial: boolean;
  propEarthquake: boolean;
  propWind: boolean;
  propFlood: boolean;
  propOther1: boolean;
  propOther1Deduct: string;
  propOther2: boolean;
  propOther2Deduct: string;
  propDeductBuilding: string;
  propDeductContents: string;
  propCovBuilding: boolean;
  propLimBuilding: string;
  propCovPersonalProp: boolean;
  propLimPersonalProp: string;
  propCovBusinessIncome: boolean;
  propLimBusinessIncome: string;
  propCovExtraExpense: boolean;
  propLimExtraExpense: string;
  propCovRentalValue: boolean;
  propLimRentalValue: string;
  propCovBlanketBuilding: boolean;
  propLimBlanketBuilding: string;
  propCovBlanketPersProp: boolean;
  propLimBlanketPersProp: string;
  propCovBlanketBldgPP: boolean;
  propLimBlanketBldgPP: string;
  propOtherCov1: boolean;
  propOtherCov1Desc: string;
  propOtherCov1Lim: string;
  propOtherCov2: boolean;
  propOtherCov2Desc: string;
  propOtherCov2Lim: string;
  // Inland Marine
  inlandInsurerLtr: string;
  inlandChecked: boolean;
  inlandNamedPerils: boolean;
  inlandOtherPerils: boolean;
  inlandOtherPerilsText: string;
  inlandTypeOfPolicy: string;
  inlandPolicyNumber: string;
  inlandEffDate: Date | null;
  inlandExpDate: Date | null;
  inlandCov1: boolean; inlandCov1Desc: string; inlandCov1Lim: string;
  inlandCov2: boolean; inlandCov2Desc: string; inlandCov2Lim: string;
  inlandCov3: boolean; inlandCov3Desc: string; inlandCov3Lim: string;
  inlandCov4: boolean; inlandCov4Desc: string; inlandCov4Lim: string;
  // Crime
  crimeInsurerLtr: string;
  crimeChecked: boolean;
  crimeTypeOfPolicy: string;
  crimeCausesOfLoss: string;
  crimePolicyNumber: string;
  crimeEffDate: Date | null;
  crimeExpDate: Date | null;
  crimeCov1: boolean; crimeCov1Desc: string; crimeCov1Lim: string;
  crimeCov2: boolean; crimeCov2Desc: string; crimeCov2Lim: string;
  crimeCov3: boolean; crimeCov3Desc: string; crimeCov3Lim: string;
  // Boiler & Machinery
  boilerInsurerLtr: string;
  boilerChecked: boolean;
  boilerPolicyNumber: string;
  boilerEffDate: Date | null;
  boilerExpDate: Date | null;
  boilerCov1: boolean; boilerCov1Desc: string; boilerCov1Lim: string;
  boilerCov2: boolean; boilerCov2Desc: string; boilerCov2Lim: string;
  // Other
  otherInsurerLtr: string;
  otherType: string;
  otherPolicyNumber: string;
  otherEffDate: Date | null;
  otherExpDate: Date | null;
  otherCov1: boolean; otherCov1Desc: string; otherCov1Lim: string;
  otherCov2: boolean; otherCov2Desc: string; otherCov2Lim: string;
  // Misc
  holderName: string;
  holderAddress: string;
  holderCity: string;
  holderStreet: string;
  holderState: string;
  holderZip: string;
  authRepSignature: string;
  // ACORD 101
  showAcord101: boolean;
  acord101Agency: string;
  acord101NamedInsured: string;
  acord101AdditionalRemarks: string;
}

// ── Date helpers ──────────────────────────────────────────────────────────────

function toPayloadDate(d: Date | null): string {
  if (!d) return '';
  const yr  = d.getFullYear();
  const mo  = String(d.getMonth() + 1).padStart(2, '0');
  const dy  = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${dy} 00:00:00`;
}

function toXmlDate(d: Date | null): string {
  if (!d) return '';
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${mo}/${dy}/${d.getFullYear()}`;
}

// ── XML helpers ───────────────────────────────────────────────────────────────

const yn = (v: boolean) => (v ? 'Yes' : 'No');

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Section XML builders ──────────────────────────────────────────────────────

function buildPropertyXml(d: Acord24FormData): string {
  return (
    `<Coverages><Acord24PropertyLiability>` +
    `<Property>${d.propChecked ? 'Yes' : ''}</Property>` +
    `<InsurerLtr>${esc(d.propInsurerLtr)}</InsurerLtr>` +
    `<Basic text="${esc(d.propDeductBuilding)}">${yn(d.propBasic)}</Basic>` +
    `<Broad>${yn(d.propBroad)}</Broad>` +
    `<Date>${esc(toXmlDate(d.certDate))}</Date>` +
    `<ProducerName>${esc(d.producerAddress)}</ProducerName>` +
    `<ProducerLineOne>${esc(d.producerStreet)}</ProducerLineOne>` +
    `<ProducerLineTwo></ProducerLineTwo>` +
    `<ProducerCity>${esc(d.producerCity)}</ProducerCity>` +
    `<ProducerState>${esc(d.producerState)}</ProducerState>` +
    `<ProducerZip>${esc(d.producerZip)}</ProducerZip>` +
    `<Name>${esc(d.insuredName)}</Name>` +
    `<LineOne>${esc(d.insuredAddress)}</LineOne>` +
    `<LineTwo></LineTwo>` +
    `<City>${esc(d.insuredCity)}</City>` +
    `<State>${esc(d.insuredState)}</State>` +
    `<Zip>${esc(d.insuredZip)}</Zip>` +
    `<Special text="${esc(d.propDeductContents)}">${yn(d.propSpecial)}</Special>` +
    `<EarthQuake text="${esc(d.propEarthquakeDeduct)}">${yn(d.propEarthquake)}</EarthQuake>` +
    `<Wind text="${esc(d.propWindDeduct)}">${yn(d.propWind)}</Wind>` +
    `<Flood text="${esc(d.propFloodDeduct)}">${yn(d.propFlood)}</Flood>` +
    `<Other1 text="${yn(d.propOther1)}" value="${esc(d.propOther1Deduct)}">${esc(d.propOther1Text)}</Other1>` +
    `<Other2 text="${yn(d.propOther2)}" value="${esc(d.propOther2Deduct)}">${esc(d.propOther2Text)}</Other2>` +
    `<ContactName>${esc(d.contactName)}</ContactName>` +
    `<Email>${esc(d.contactEmail)}</Email>` +
    `<Fax>${esc(d.contactFax)}</Fax>` +
    `<Phone>${esc(d.contactPhone)}</Phone>` +
    `<CustomerId>${esc(d.producerCustomerId)}</CustomerId>` +
    `<CertificateNumber>${esc(d.certNumber)}</CertificateNumber>` +
    `<RevisionNumber>${esc(d.revisionNumber)}</RevisionNumber>` +
    `<InsurerA>${esc(d.insurerA)}</InsurerA>` +
    `<InsurerB>${esc(d.insurerB)}</InsurerB>` +
    `<InsurerC>${esc(d.insurerC)}</InsurerC>` +
    `<InsurerD>${esc(d.insurerD)}</InsurerD>` +
    `<InsurerE>${esc(d.insurerE)}</InsurerE>` +
    `<InsurerF>${esc(d.insurerF)}</InsurerF>` +
    `<InsurerAId>${esc(d.insurerAId ?? '')}</InsurerAId>` +
    `<InsurerBId>${esc(d.insurerBId ?? '')}</InsurerBId>` +
    `<InsurerCId>${esc(d.insurerCId ?? '')}</InsurerCId>` +
    `<InsurerDId>${esc(d.insurerDId ?? '')}</InsurerDId>` +
    `<InsurerEId>${esc(d.insurerEId ?? '')}</InsurerEId>` +
    `<InsurerFId>${esc(d.insurerFId ?? '')}</InsurerFId>` +
    `<LocationOfPremises>${esc(d.locationProperty || d.descriptionOfProperty)}</LocationOfPremises>` +
    `<DescriptionOfProperty>${esc(d.descriptionOfProperty)}</DescriptionOfProperty>` +
    `<SpecialConditions>${esc(d.specialConditions)}</SpecialConditions>` +
    `<PolicyNumber>${esc(d.propPolicyNumber)}</PolicyNumber>` +
    `<EffectiveDate>${esc(toXmlDate(d.propEffDate))}</EffectiveDate>` +
    `<ExpirationDate>${esc(toXmlDate(d.propExpDate))}</ExpirationDate>` +
    `</Acord24PropertyLiability></Coverages>` +
    `<Limits><Acord24PropertyLiability>` +
    `<Building text="${yn(d.propCovBuilding)}">${esc(d.propLimBuilding)}</Building>` +
    `<PersonalProperty text="${yn(d.propCovPersonalProp)}">${esc(d.propLimPersonalProp)}</PersonalProperty>` +
    `<BusinessIncome text="${yn(d.propCovBusinessIncome)}">${esc(d.propLimBusinessIncome)}</BusinessIncome>` +
    `<ExtraExpense text="${yn(d.propCovExtraExpense)}">${esc(d.propLimExtraExpense)}</ExtraExpense>` +
    `<RentalValue text="${yn(d.propCovRentalValue)}">${esc(d.propLimRentalValue)}</RentalValue>` +
    `<BlanketBuilding text="${yn(d.propCovBlanketBuilding)}">${esc(d.propLimBlanketBuilding)}</BlanketBuilding>` +
    `<BlanketPersProp text="${yn(d.propCovBlanketPersProp)}">${esc(d.propLimBlanketPersProp)}</BlanketPersProp>` +
    `<BlanketBldgPp text="${yn(d.propCovBlanketBldgPP)}">${esc(d.propLimBlanketBldgPP)}</BlanketBldgPp>` +
    `<Other1 text="${yn(d.propOtherCov1)}" value="${esc(d.propOtherCov1Lim)}">${esc(d.propOtherCov1Desc)}</Other1>` +
    `<Other2 text="${yn(d.propOtherCov2)}" value="${esc(d.propOtherCov2Lim)}">${esc(d.propOtherCov2Desc)}</Other2>` +
    `</Acord24PropertyLiability></Limits>`
  );
}

function buildInlandXml(d: Acord24FormData): string {
  return (
    `<Coverages><Acord24InlandMarineLiability>` +
    `<InlandMarine>${d.inlandChecked ? 'Yes' : ''}</InlandMarine>` +
    `<InsurerLtr>${esc(d.inlandInsurerLtr)}</InsurerLtr>` +
    `<NamedPerils>${yn(d.inlandNamedPerils)}</NamedPerils>` +
    `<Other text="${esc(d.inlandOtherPerilsText)}">${yn(d.inlandOtherPerils)}</Other>` +
    `<TypeOfPolicy>${esc(d.inlandTypeOfPolicy)}</TypeOfPolicy>` +
    `<ContactName>${esc(d.contactName)}</ContactName>` +
    `<Email>${esc(d.contactEmail)}</Email>` +
    `<Fax>${esc(d.contactFax)}</Fax>` +
    `<Phone>${esc(d.contactPhone)}</Phone>` +
    `<InsurerA>${esc(d.insurerA)}</InsurerA>` +
    `<InsurerB>${esc(d.insurerB)}</InsurerB>` +
    `<InsurerC>${esc(d.insurerC)}</InsurerC>` +
    `<InsurerD>${esc(d.insurerD)}</InsurerD>` +
    `<InsurerE>${esc(d.insurerE)}</InsurerE>` +
    `<InsurerF>${esc(d.insurerF)}</InsurerF>` +
    `<InsurerAId>${esc(d.insurerAId ?? '')}</InsurerAId>` +
    `<InsurerBId>${esc(d.insurerBId ?? '')}</InsurerBId>` +
    `<InsurerCId>${esc(d.insurerCId ?? '')}</InsurerCId>` +
    `<InsurerDId>${esc(d.insurerDId ?? '')}</InsurerDId>` +
    `<InsurerEId>${esc(d.insurerEId ?? '')}</InsurerEId>` +
    `<InsurerFId>${esc(d.insurerFId ?? '')}</InsurerFId>` +
    `<LocationOfPremises>${esc(d.locationProperty || d.descriptionOfProperty)}</LocationOfPremises>` +
    `<DescriptionOfProperty>${esc(d.descriptionOfProperty)}</DescriptionOfProperty>` +
    `<SpecialConditions>${esc(d.specialConditions)}</SpecialConditions>` +
    `</Acord24InlandMarineLiability></Coverages>` +
    `<Limits><Acord24InlandMarineLiability>` +
    `<Other1 text="${yn(d.inlandCov1)}" value="${esc(d.inlandCov1Lim)}">${esc(d.inlandCov1Desc)}</Other1>` +
    `<Other2 text="${yn(d.inlandCov2)}" value="${esc(d.inlandCov2Lim)}">${esc(d.inlandCov2Desc)}</Other2>` +
    `<Other3 text="${yn(d.inlandCov3)}" value="${esc(d.inlandCov3Lim)}">${esc(d.inlandCov3Desc)}</Other3>` +
    `<Other4 text="${yn(d.inlandCov4)}" value="${esc(d.inlandCov4Lim)}">${esc(d.inlandCov4Desc)}</Other4>` +
    `</Acord24InlandMarineLiability></Limits>`
  );
}

function buildCrimeXml(d: Acord24FormData): string {
  return (
    `<Coverages><Acord24CrimeLiability>` +
    `<Crime>${d.crimeChecked ? 'Yes' : ''}</Crime>` +
    `<InsurerLtr>${esc(d.crimeInsurerLtr)}</InsurerLtr>` +
    `<TypeOfPolicy>${esc(d.crimeTypeOfPolicy)}</TypeOfPolicy>` +
    `<ContactName>${esc(d.contactName)}</ContactName>` +
    `<Email>${esc(d.contactEmail)}</Email>` +
    `<Fax>${esc(d.contactFax)}</Fax>` +
    `<Phone>${esc(d.contactPhone)}</Phone>` +
    `<InsurerA>${esc(d.insurerA)}</InsurerA>` +
    `<InsurerB>${esc(d.insurerB)}</InsurerB>` +
    `<InsurerC>${esc(d.insurerC)}</InsurerC>` +
    `<InsurerD>${esc(d.insurerD)}</InsurerD>` +
    `<InsurerE>${esc(d.insurerE)}</InsurerE>` +
    `<InsurerF>${esc(d.insurerF)}</InsurerF>` +
    `<InsurerAId>${esc(d.insurerAId ?? '')}</InsurerAId>` +
    `<InsurerBId>${esc(d.insurerBId ?? '')}</InsurerBId>` +
    `<InsurerCId>${esc(d.insurerCId ?? '')}</InsurerCId>` +
    `<InsurerDId>${esc(d.insurerDId ?? '')}</InsurerDId>` +
    `<InsurerEId>${esc(d.insurerEId ?? '')}</InsurerEId>` +
    `<InsurerFId>${esc(d.insurerFId ?? '')}</InsurerFId>` +
    `<LocationOfPremises>${esc(d.locationProperty || d.descriptionOfProperty)}</LocationOfPremises>` +
    `<DescriptionOfProperty>${esc(d.descriptionOfProperty)}</DescriptionOfProperty>` +
    `<SpecialConditions>${esc(d.specialConditions)}</SpecialConditions>` +
    `</Acord24CrimeLiability></Coverages>` +
    `<Limits><Acord24CrimeLiability>` +
    `<Other1 text="${yn(d.crimeCov1)}" value="${esc(d.crimeCov1Lim)}">${esc(d.crimeCov1Desc)}</Other1>` +
    `<Other2 text="${yn(d.crimeCov2)}" value="${esc(d.crimeCov2Lim)}">${esc(d.crimeCov2Desc)}</Other2>` +
    `<Other3 text="${yn(d.crimeCov3)}" value="${esc(d.crimeCov3Lim)}">${esc(d.crimeCov3Desc)}</Other3>` +
    `</Acord24CrimeLiability></Limits>`
  );
}

function buildBoilerXml(d: Acord24FormData): string {
  return (
    `<Coverages><Acord24BoilerLiability>` +
    `<Boiler>${d.boilerChecked ? 'Yes' : ''}</Boiler>` +
    `<InsurerLtr>${esc(d.boilerInsurerLtr)}</InsurerLtr>` +
    `<ContactName>${esc(d.contactName)}</ContactName>` +
    `<Email>${esc(d.contactEmail)}</Email>` +
    `<Fax>${esc(d.contactFax)}</Fax>` +
    `<Phone>${esc(d.contactPhone)}</Phone>` +
    `<InsurerA>${esc(d.insurerA)}</InsurerA>` +
    `<InsurerB>${esc(d.insurerB)}</InsurerB>` +
    `<InsurerC>${esc(d.insurerC)}</InsurerC>` +
    `<InsurerD>${esc(d.insurerD)}</InsurerD>` +
    `<InsurerE>${esc(d.insurerE)}</InsurerE>` +
    `<InsurerF>${esc(d.insurerF)}</InsurerF>` +
    `<InsurerAId>${esc(d.insurerAId ?? '')}</InsurerAId>` +
    `<InsurerBId>${esc(d.insurerBId ?? '')}</InsurerBId>` +
    `<InsurerCId>${esc(d.insurerCId ?? '')}</InsurerCId>` +
    `<InsurerDId>${esc(d.insurerDId ?? '')}</InsurerDId>` +
    `<InsurerEId>${esc(d.insurerEId ?? '')}</InsurerEId>` +
    `<InsurerFId>${esc(d.insurerFId ?? '')}</InsurerFId>` +
    `<LocationOfPremises>${esc(d.locationProperty || d.descriptionOfProperty)}</LocationOfPremises>` +
    `<DescriptionOfProperty>${esc(d.descriptionOfProperty)}</DescriptionOfProperty>` +
    `<SpecialConditions>${esc(d.specialConditions)}</SpecialConditions>` +
    `</Acord24BoilerLiability></Coverages>` +
    `<Limits><Acord24BoilerLiability>` +
    `<Other1 text="${yn(d.boilerCov1)}" value="${esc(d.boilerCov1Lim)}">${esc(d.boilerCov1Desc)}</Other1>` +
    `<Other2 text="${yn(d.boilerCov2)}" value="${esc(d.boilerCov2Lim)}">${esc(d.boilerCov2Desc)}</Other2>` +
    `</Acord24BoilerLiability></Limits>`
  );
}

function buildOtherXml(d: Acord24FormData): string {
  return (
    `<Coverages><Acord24OtherLiability>` +
    `<Other>${esc(d.otherType)}</Other>` +
    `<InsurerLtr>${esc(d.otherInsurerLtr)}</InsurerLtr>` +
    `<InsurerA>${esc(d.insurerA)}</InsurerA>` +
    `<InsurerB>${esc(d.insurerB)}</InsurerB>` +
    `<InsurerC>${esc(d.insurerC)}</InsurerC>` +
    `<InsurerD>${esc(d.insurerD)}</InsurerD>` +
    `<InsurerE>${esc(d.insurerE)}</InsurerE>` +
    `<InsurerF>${esc(d.insurerF)}</InsurerF>` +
    `<InsurerAId>${esc(d.insurerAId ?? '')}</InsurerAId>` +
    `<InsurerBId>${esc(d.insurerBId ?? '')}</InsurerBId>` +
    `<InsurerCId>${esc(d.insurerCId ?? '')}</InsurerCId>` +
    `<InsurerDId>${esc(d.insurerDId ?? '')}</InsurerDId>` +
    `<InsurerEId>${esc(d.insurerEId ?? '')}</InsurerEId>` +
    `<InsurerFId>${esc(d.insurerFId ?? '')}</InsurerFId>` +
    `<LocationOfPremises>${esc(d.locationProperty || d.descriptionOfProperty)}</LocationOfPremises>` +
    `<DescriptionOfProperty>${esc(d.descriptionOfProperty)}</DescriptionOfProperty>` +
    `<SpecialConditions>${esc(d.specialConditions)}</SpecialConditions>` +
    `</Acord24OtherLiability></Coverages>` +
    `<Limits><Acord24OtherLiability>` +
    `<Other1 text="${yn(d.otherCov1)}" value="${esc(d.otherCov1Lim)}">${esc(d.otherCov1Desc)}</Other1>` +
    `<Other2 text="${yn(d.otherCov2)}" value="${esc(d.otherCov2Lim)}">${esc(d.otherCov2Desc)}</Other2>` +
    `</Acord24OtherLiability></Limits>`
  );
}

function buildAcord101Xml(d: Acord24FormData): string {
  return (
    `<Coverages><Acord101AdditionalRemarks>` +
    `<RemarksText>${esc(d.acord101AdditionalRemarks)}</RemarksText>` +
    `</Acord101AdditionalRemarks></Coverages><Limits />`
  );
}

function buildCertHolderXml(d: Acord24FormData): string {
  return (
    `<Coverages><CertificateHolderInformationCoverage>` +
    `<CertificateHolder>` +
    `<Name>${esc(d.holderName)}</Name>` +
    `<Address>${esc(d.holderAddress)}</Address>` +
    `<City>${esc(d.holderCity)}</City>` +
    `<Street>${esc(d.holderStreet)}</Street>` +
    `<State>${esc(d.holderState)}</State>` +
    `<Zip>${esc(d.holderZip)}</Zip>` +
    `</CertificateHolder>` +
    `<AdditionalInterest>` +
    `<NameAndAddress></NameAndAddress>` +
    `<AdditionalInsured>false</AdditionalInsured>` +
    `<LendersLossPayable>false</LendersLossPayable>` +
    `<LossPayee>false</LossPayee>` +
    `<Mortage>false</Mortage>` +
    `<Other>false</Other>` +
    `<OtherDescription></OtherDescription>` +
    `<LoanNumber></LoanNumber>` +
    `<LenderServicingAgentNameAndAddress></LenderServicingAgentNameAndAddress>` +
    `</AdditionalInterest>` +
    `</CertificateHolderInformationCoverage></Coverages>`
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Builds the `Coverages` array for the create-master-certificate payload
 * from ACORD 24 form state. One entry is emitted per coverage section that
 * has a policy number filled in, plus an ACORD 101 entry when applicable.
 */
export function buildAcord24Coverages(d: Acord24FormData, formNumber: number = 24): CoverageEntry[] {
  const entries: CoverageEntry[] = [];
  const sig  = d.authRepSignature || null;
  const base = formNumber;

  const insurerIdByLetter: Record<string, string | null> = {
    A: d.insurerAId ?? null, B: d.insurerBId ?? null, C: d.insurerCId ?? null,
    D: d.insurerDId ?? null, E: d.insurerEId ?? null, F: d.insurerFId ?? null,
  };

  entries.push({
    PolicyNumber: d.propPolicyNumber,
    NaicId: insurerIdByLetter[d.propInsurerLtr] ?? null,
    EffectiveDate: toPayloadDate(d.propEffDate),
    ExpirationDate: toPayloadDate(d.propExpDate),
    AcordFormNumber: base,
    CoveragesAndLimits: buildPropertyXml(d),
    SignatureId: sig,
  });

  entries.push({
    PolicyNumber: d.inlandPolicyNumber,
    NaicId: insurerIdByLetter[d.inlandInsurerLtr] ?? null,
    EffectiveDate: toPayloadDate(d.inlandEffDate),
    ExpirationDate: toPayloadDate(d.inlandExpDate),
    AcordFormNumber: base,
    CoveragesAndLimits: buildInlandXml(d),
    SignatureId: sig,
  });

  entries.push({
    PolicyNumber: d.crimePolicyNumber,
    NaicId: insurerIdByLetter[d.crimeInsurerLtr] ?? null,
    EffectiveDate: toPayloadDate(d.crimeEffDate),
    ExpirationDate: toPayloadDate(d.crimeExpDate),
    AcordFormNumber: base,
    CoveragesAndLimits: buildCrimeXml(d),
    SignatureId: sig,
  });

  entries.push({
    PolicyNumber: d.boilerPolicyNumber,
    NaicId: insurerIdByLetter[d.boilerInsurerLtr] ?? null,
    EffectiveDate: toPayloadDate(d.boilerEffDate),
    ExpirationDate: toPayloadDate(d.boilerExpDate),
    AcordFormNumber: base,
    CoveragesAndLimits: buildBoilerXml(d),
    SignatureId: sig,
  });

  entries.push({
    PolicyNumber: d.otherPolicyNumber,
    NaicId: insurerIdByLetter[d.otherInsurerLtr] ?? null,
    EffectiveDate: toPayloadDate(d.otherEffDate),
    ExpirationDate: toPayloadDate(d.otherExpDate),
    AcordFormNumber: base,
    CoveragesAndLimits: buildOtherXml(d),
    SignatureId: sig,
  });

  if (d.showAcord101) {
    entries.push({
      PolicyNumber: '',
      NaicId: null,
      EffectiveDate: '',
      ExpirationDate: '',
      AcordFormNumber: 101,
      CoveragesAndLimits: buildAcord101Xml(d),
      SignatureId: sig,
    });
  }

  if (d.holderName) {
    entries.push({
      PolicyNumber: '',
      NaicId: null,
      EffectiveDate: '',
      ExpirationDate: '',
      AcordFormNumber: 2716,
      CoveragesAndLimits: buildCertHolderXml(d),
      SignatureId: sig,
    });
  }

  return entries;
}
